import { RecoveryView, type RecoveryViewAction } from "@kovernow/react-ui/components/recovery-view";
import type { RecoveryAction, RecoveryActionName } from "@kovernow/flows/recovery";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "../core/app-context";
import { isSafeAppPath } from "../core/safe-path";
import { reportRecovery } from "../core/sentry";
import { useT } from "../core/use-t";

/** Where each RecoveryAction leads in W-0 (placeholder routes for later journeys). */
const ACTION_ROUTES: Partial<Record<RecoveryAction["action"], string>> = {
  reauthenticate: "/sign-in",
  sign_in: "/sign-in",
  update_payment: "/account/payment-methods",
  switch_payment_method: "/account/payment-methods",
  check_order_status: "/",
  upload_documents: "/vault",
  update_item_details: "/vault",
  verify_identity: "/account/verify",
};

/**
 * RecoveryAction → RecoveryView. Every failure gets a next step; every
 * contact_support shows "Reference: {correlationId}".
 */
export function Recovery({
  action,
  onRetry,
  live = true,
  handlers,
  retryable = false,
  message,
  support: supportOverride,
}: {
  action: RecoveryAction;
  /** A message already in the customer's language (e.g. a validate response's), instead of `messageKey`. */
  message?: string;
  onRetry: () => void;
  /** Screen-specific handling for an action (e.g. resend_code, sign_in with the email). */
  handlers?: Partial<Record<RecoveryActionName, () => void>>;
  /** false when rendered inside a persistent aria-live wrapper. */
  live?: boolean;
  /**
   * A read that can always be retried (e.g. a screen's data load): offer
   * "Try again" first even when the action is contact_support.
   */
  retryable?: boolean;
  /**
   * Replaces the default "Contact support" mailto (e.g. W-8 HK verify's
   * "Need help verifying?", which opens a support case).
   */
  support?: RecoveryViewAction;
}) {
  const t = useT();
  const { config } = useServices();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const [shownAt] = useState(() => Date.now());

  useEffect(() => reportRecovery(action), [action]);

  const waitMs = action.action === "retry_with_delay" ? (action.delayMs ?? 0) - (now - shownAt) : 0;
  useEffect(() => {
    if (waitMs <= 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [waitMs]);

  const supportHref = `mailto:${config.brand.supportEmail}${
    action.correlationId ? `?subject=${encodeURIComponent(t("errors.reference", { id: action.correlationId }))}` : ""
  }`;
  const support: RecoveryViewAction = supportOverride ?? {
    id: "contact_support",
    label: t("recovery.contact_support"),
    href: supportHref,
  };

  const actions: RecoveryViewAction[] = [];
  const handler = handlers?.[action.action];
  if (handler) {
    actions.push({ id: action.action, label: t(`recovery.${action.action}`), onSelect: handler });
  } else {
    switch (action.action) {
      case "retry":
      case "re_fetch_offers":
      case "resend_code":
        actions.push({ id: action.action, label: t(`recovery.${action.action}`), onSelect: onRetry });
        break;
      case "retry_with_delay":
        actions.push({
          id: "retry_with_delay",
          label: t("recovery.retry_with_delay"),
          onSelect: onRetry,
          disabledReason: waitMs > 0 ? t("recovery.retryIn", { seconds: Math.ceil(waitMs / 1000) }) : undefined,
        });
        break;
      case "contact_support":
        break;
      default: {
        const to = isSafeAppPath(action.deepLink) ? action.deepLink : ACTION_ROUTES[action.action] ?? "/";
        actions.push({ id: action.action, label: t(`recovery.${action.action}`), onSelect: () => navigate(to) });
      }
    }
  }
  if (retryable && !actions.some((a) => a.id === "retry" || a.id === "retry_with_delay")) {
    actions.unshift({ id: "retry", label: t("recovery.retry"), onSelect: onRetry });
  }
  actions.push(support);

  const values = { tenant: config.brand.name };
  return (
    <RecoveryView
      live={live}
      kind={action.kind}
      message={message ?? t([action.messageKey, "errors.generic"], values)}
      impact={action.impactKey ? t(action.impactKey) : undefined}
      reference={
        action.action === "contact_support" || action.correlationId
          ? t("errors.reference", { id: action.correlationId ?? action.errorCode })
          : undefined
      }
      actions={actions}
    />
  );
}
